import React, { useState, useEffect } from 'react';
import { AISHA_API_BASE } from '@/constants';
import { 
  CalendarIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlusCircleIcon,
  ExternalLinkIcon,
  ArrowLeftIcon
} from './icons/HeroIcons';

interface InformationItem {
  id: number;
  date: string;
  title: string;
  type: 'notice' | 'menu_add' | 'error';
  type_display: string;
  content: {
    message?: string;
    category?: string;
    menu_name?: string;
    sample_input_url?: string;
    sample_output_url?: string;
    error_message?: string;
    error_time?: string;
    fix_time?: string;
  };
  created_at: string;
  updated_at: string;
}

interface InformationListResponse {
  success: boolean;
  items: InformationItem[];
  total: number;
  page: number;
  total_pages: number;
  per_page: number;
}

interface InformationDetailModalProps {
  item: InformationItem | null;
  isOpen: boolean;
  onClose: () => void;
}

const InformationDetailModal: React.FC<InformationDetailModalProps> = ({ item, isOpen, onClose }) => {
  if (!isOpen || !item) return null;

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'notice':
        return <InformationCircleIcon className="w-6 h-6" />;
      case 'menu_add':
        return <PlusCircleIcon className="w-6 h-6" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-6 h-6" />;
      default:
        return <InformationCircleIcon className="w-6 h-6" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'notice':
        return 'text-blue-400';
      case 'menu_add':
        return 'text-green-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-2">
            <div className={getTypeColor(item.type)}>
              {getTypeIcon(item.type)}
            </div>
            <span className="text-sm text-gray-400">{item.type_display}</span>
            <span className="text-sm text-gray-500">•</span>
            <span className="text-sm text-gray-400">{item.date}</span>
          </div>
          <h2 className="text-xl font-bold text-white">{item.title}</h2>
        </div>

        <div className="border-t border-gray-700 pt-4">
          {item.type === 'notice' && (
            <div className="text-gray-300 whitespace-pre-wrap">
              {item.content.message}
            </div>
          )}

          {item.type === 'menu_add' && (
            <div className="space-y-3">
              {item.content.message && (
                <div className="text-gray-300 whitespace-pre-wrap">
                  {item.content.message}
                </div>
              )}
              <div>
                <span className="text-sm text-gray-400">カテゴリ:</span>
                <span className="ml-2 text-gray-300">{item.content.category}</span>
              </div>
              <div>
                <span className="text-sm text-gray-400">メニュー名:</span>
                <span className="ml-2 text-gray-300">{item.content.menu_name}</span>
              </div>
              {(item.content.sample_input_url || item.content.sample_output_url) && (
                <div className="mt-4 space-y-2">
                  {item.content.sample_input_url && (
                    <div>
                      <p className="text-sm text-gray-400 mb-2">元画像:</p>
                      <img 
                        src={item.content.sample_input_url} 
                        alt="元画像" 
                        className="max-w-full h-auto rounded-lg"
                      />
                    </div>
                  )}
                  {item.content.sample_output_url && (
                    <div>
                      <p className="text-sm text-gray-400 mb-2">生成後:</p>
                      <img 
                        src={item.content.sample_output_url} 
                        alt="生成後" 
                        className="max-w-full h-auto rounded-lg"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {item.type === 'error' && (
            <div className="space-y-3">
              <div className="text-gray-300 whitespace-pre-wrap">
                {item.content.error_message}
              </div>
              {item.content.error_time && (
                <div>
                  <span className="text-sm text-gray-400">発生日時:</span>
                  <span className="ml-2 text-gray-300">
                    {new Date(item.content.error_time).toLocaleString('ja-JP')}
                  </span>
                </div>
              )}
              {item.content.fix_time && (
                <div>
                  <span className="text-sm text-gray-400">修正日時:</span>
                  <span className="ml-2 text-gray-300">
                    {new Date(item.content.fix_time).toLocaleString('ja-JP')}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

const InformationList: React.FC = () => {
  const [informationList, setInformationList] = useState<InformationItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<InformationItem | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchInformation = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '20'
      });

      const response = await fetch(`${AISHA_API_BASE}/admin/information/?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data: InformationListResponse = await response.json();

      if (data.success) {
        setInformationList(data.items);
        setTotalPages(data.total_pages);
      }
    } catch (error) {
      console.error('Failed to fetch information:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInformation();
  }, [currentPage]);

  const handleItemClick = (item: InformationItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'notice':
        return <InformationCircleIcon className="w-5 h-5" />;
      case 'menu_add':
        return <PlusCircleIcon className="w-5 h-5" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-5 h-5" />;
      default:
        return <InformationCircleIcon className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'notice':
        return 'text-blue-400';
      case 'menu_add':
        return 'text-green-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-indigo-300">お知らせ</h1>
          <div className="flex items-center gap-3">
            <a
              href="https://ai-sha.jp/lp/#contact"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              お問い合わせフォームへ
              <ExternalLinkIcon className="w-4 h-4" />
            </a>
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              戻る
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {informationList.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className="bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors cursor-pointer border border-gray-700 hover:border-indigo-500"
                >
                  <div className="flex items-start gap-4">
                    <div className={`mt-1 ${getTypeColor(item.type)}`}>
                      {getTypeIcon(item.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1 text-sm text-gray-400">
                        <span>{item.type_display}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {item.date}
                        </span>
                      </div>
                      <h3 className="text-lg font-medium text-white hover:text-indigo-400 transition-colors">
                        {item.title}
                      </h3>
                      {/* Message preview */}
                      {item.type === 'notice' && item.content.message && (
                        <p className="text-sm text-gray-400 mt-2 line-clamp-2">
                          {item.content.message}
                        </p>
                      )}
                      {item.type === 'error' && item.content.error_message && (
                        <p className="text-sm text-gray-400 mt-2 line-clamp-2">
                          {item.content.error_message}
                        </p>
                      )}
                      {item.type === 'menu_add' && (
                        <>
                          {item.content.message && (
                            <p className="text-sm text-gray-400 mt-2 line-clamp-2">
                              {item.content.message}
                            </p>
                          )}
                          <div className="mt-2 flex gap-2">
                            {item.content.sample_input_url && (
                              <img 
                                src={item.content.sample_input_url} 
                                alt="元画像" 
                                className="w-16 h-16 object-cover rounded"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            )}
                            {item.content.sample_output_url && (
                              <img 
                                src={item.content.sample_output_url} 
                                alt="生成後" 
                                className="w-16 h-16 object-cover rounded"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 rounded-lg transition-colors ${
                      page === currentPage
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

      </div>

      <InformationDetailModal
        item={selectedItem}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedItem(null);
        }}
      />
    </div>
  );
};

export default InformationList;